import { useMemo, useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { createUpdatedChips, hasActiveChips } from '@helpers/filters';
import { updateState } from '@helpers/filters';
import { SYNC_VIEW } from '@constants/profiles';
import { useTranslation, useSyncProfilesFilters } from '@hooks';

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    filter,
    setFilter,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const [filterChips, setFilterChips] = useState({
        companies: [],
        platforms: [],
        views: [],
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const { locationsOptions, getPlatformsOptions, getPlatformsValues } = useSyncProfilesFilters();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const options = useMemo(() => {
        const viewsOptions = [
            { value: SYNC_VIEW.SYNCED, label: t('common.synced') },
            { value: SYNC_VIEW.CONFLICT, label: t('common.conflict') },
            { value: SYNC_VIEW.NOT_PUBLISHED, label: t('common.notPublished') },
            { value: SYNC_VIEW.IN_PROGRESS, label: t('common.inProgress') },
            { value: SYNC_VIEW.DISABLED, label: t('common.disabled') },
        ];

        return {
            companies: locationsOptions,
            platforms: getPlatformsOptions(filter.companies.map(item => item.id)),
            views: viewsOptions,
        };
    }, [filter, locationsOptions]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;
            increment = filterChips[key]?.length > 0 ? 1 : 0;

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            const selectedValuesIds = selectedValues.length ? selectedValues.map(item => item.id) : [];

            const isAllPlatformsSelected = filter.platforms.length === options.platforms.length;
            const platformsIds = getPlatformsValues(selectedValuesIds);

            setFilter(prev => ({
                ...prev,
                companies: selectedValues,
                platforms: updateState(prev?.platforms, platformsIds, isAllPlatformsSelected),
            }));
        },
        platforms: value => setFilter(prev => ({ ...prev, platforms: value })),
        views: value => setFilter(prev => ({ ...prev, views: value })),
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => {
        update.companies(options.companies);
        setFilter(prev => ({
            ...prev,
            platforms: getPlatformsValues(prev.companies.map(item => item.id)),
            views: options.views.map(option => option.value),
        }));
    };

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        for (let key in filter) {
            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: key === 'companies' ? 'autocomplete' : 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: !filter[key].length,
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = Object.keys(filter).every(key => {
        if (Array.isArray(filter[key])) {
            return !!filter[key].length;
        } else {
            return true;
        }
    });

    const hasDraftFilters = useMemo(
        () => !isValid || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            <FiltersModal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </FiltersModal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
